import { ErrorLike, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { DocumentNode } from 'graphql';
import { useEffect, useRef } from 'react';

type Options<TData, TVariables extends OperationVariables> = Omit<
  useQuery.Options<TData, TVariables>,
  'returnPartialData'
> & {
  returnPartialData?: false;
  onError?: (error: ErrorLike) => void;
};

export const useSkippableQuery = <
  TData,
  TVariables extends OperationVariables = OperationVariables
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  { onError, ...options }: Options<TData, TVariables>
) => {
  // Apollo's optional options tuple depends on the variables type. This wrapper
  // always receives options, including required variables for typed documents.
  const args = [options] as Record<string, never> extends TVariables
    ? [options?: useQuery.Options<TData, TVariables>]
    : [options: useQuery.Options<TData, TVariables>];
  const result = useQuery<TData, TVariables>(query, ...args);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    if (result.error) onErrorRef.current?.(result.error);
  }, [result.error]);

  return { ...result, loading: result.loading && !options.skip };
};
